<script setup>
import {onMounted, ref} from "vue";
import {usePaymentMethodsStore} from "@/stores/payment.js";
import {showMessage} from "@/utils/showMessage.js";
import {useConfirm} from "primevue/useconfirm";

const props = defineProps({
  user: {
    type: Object,
    required: true
  },
  methods: {
    type: Array,
    required: true
  }
});
const emit = defineEmits(['editMethod', 'refresh']);
const confirm = useConfirm();
const paymentMethodsStore = usePaymentMethodsStore();
const paymentMethods = ref([]);
const loading = ref(false);
const copiedKey = ref(null);

const copyToClipboard = async (text, key) => {
  try {
    await navigator.clipboard.writeText(text);

    copiedKey.value = key;

    setTimeout(() => {
      if (copiedKey.value === key) {
        copiedKey.value = null;
      }
    }, 2000);
  } catch (error) {
    console.error(error);
  }
};

const editMethod = (method) => {
  emit('editMethod', method)
}

const removePaymentMethod = async (method) => {
  confirm.require({
    group: 'templating',
    header: 'Confirmation',
    message: {
      title: `You are deleting payment method? (${method.name})`,
      text: 'Are you sure?'
    },
    icon: 'pi pi-exclamation-circle',
    rejectProps: {
      class: '!border-slate-500 !py-2 !px-4 !text-slate-300',
      label: 'Cancel',
      outlined: true,
      size: 'small'
    },
    acceptProps: {
      class: '!border-blue-500 !py-2 !px-4 !text-blue-500 hover:!bg-blue-500 hover:!text-slate-200',
      outlined: true,
      label: 'Delete',
      size: 'small'
    },
    accept: async () => {
      try {
        const res = await paymentMethodsStore.action_removePaymentMethod2({
          profile: {id: props.user.id},
          billing: {id: method.id}
        });
        if (res.result === true) {
          showMessage('Payment Method Deleted', 'success');
          emit('refresh')
        } else {
          showMessage(res, 'error');
        }
      } catch (err) {
        //toast.add({severity: 'error', summary: 'Failed', detail: err, life: 3000});
        showMessage(err, 'error');
      } finally {
      }
    },
    reject: () => {
      return
    }
  });
}
</script>

<template>
  <div v-for="(method, index) in methods"
       :key="index" class="relative p-2 bg-slate-800 border-slate-600 rounded-xl overflow-hidden">
    <!--    <img
            :src="method.type === 'Rial' ? chip : tether"
            class="absolute right-2 top-1 w-12 h-12 object-contain opacity-30"
            alt=""
        />-->

    <div class="flex flex-row gap-2">
      <div class="flex-1">
        <div class="relative z-10 flex flex-col gap-2">
          <p class="text-base text-blue-400">{{ method.type }}: {{ method.name }}</p>
          <div class="flex flex-col justify-center">
            <div class="flex flex-row gap-2 items-center">
              <span class="text-slate-400 text-xs">
                {{ method.type === 'Rial' ? 'iban' : 'Network' }}
              </span>
              <template v-if="method.type === 'Rial'">
                <template v-if="copiedKey !== `${index}-first`">
                  <svg
                      @click="copyToClipboard(
      method.type === 'Rial'
        ? method.rial.iban
        : method.crypto.network,
      `${index}-first`
    )"
                      xmlns="http://www.w3.org/2000/svg"
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      class="lucide lucide-copy-icon lucide-copy cursor-pointer"
                  >
                    <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                    <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                  </svg>
                </template>
                <template v-else>
                  <div class="flex flex-row gap-1 items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                         stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-check-icon lucide-check stroke-green-500">
                      <path d="M20 6 9 17l-5-5"/>
                    </svg>
                    <span class="text-[.7rem] leading-none text-green-500">copied!</span>
                  </div>
                </template>
              </template>
            </div>
            <span class="break-all">{{
                method.type === 'Rial' ? method.rial.iban : method.crypto.network
              }}</span>
          </div>
          <div class="flex flex-col justify-center">
            <div class="flex flex-row gap-2 items-center">
              <span class="text-slate-400 text-xs">
                {{ method.type === 'Rial' ? 'Card Number' : 'Address' }}
              </span>
              <template v-if="copiedKey !== `${index}-second`">
                <svg
                    @click="copyToClipboard(
      method.type === 'Rial'
        ? method.rial.cardNumber
        : method.crypto.address,
      `${index}-second`
    )"
                    xmlns="http://www.w3.org/2000/svg"
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="lucide lucide-copy-icon lucide-copy cursor-pointer"
                >
                  <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                  <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                </svg>
              </template>

              <template v-else>
                <div class="flex flex-row gap-1 items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-check-icon lucide-check stroke-green-500">
                    <path d="M20 6 9 17l-5-5"/>
                  </svg>
                  <span class="text-[.7rem] leading-none text-green-500">copied!</span>
                </div>
              </template>
            </div>
            <span class="break-all">
              {{ method.type === 'Rial' ? method.rial.cardNumber : method.crypto.address }}
            </span>
          </div>
        </div>
      </div>
      <div class="flex flex-col gap-2">
        <svg @click="editMethod(method)" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"
             fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             class="lucide lucide-square-pen-icon lucide-square-pen cursor-pointer">
          <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
          <path
              d="M18.375 2.625a1 1 0 0 1 3 3l-9.013 9.014a2 2 0 0 1-.853.505l-2.873.84a.5.5 0 0 1-.62-.62l.84-2.873a2 2 0 0 1 .506-.852z"/>
        </svg>
        <svg @click="removePaymentMethod(method)" xmlns="http://www.w3.org/2000/svg" width="14" height="14"
             viewBox="0 0 24 24" fill="none"
             stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             class="lucide lucide-trash2-icon lucide-trash-2 cursor-pointer">
          <path d="M10 11v6"/>
          <path d="M14 11v6"/>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/>
          <path d="M3 6h18"/>
          <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
        </svg>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">

</style>